import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import axios from "axios";
import { useAuth } from "./AuthContext";

interface Notification {
  id: number;
  tracker_id: number;
  message: string;
  is_read: boolean;
  created_at: string;
}

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  loadNotifications: () => Promise<void>;
  deleteNotification: (id: number) => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType | undefined>(
  undefined,
);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      "useNotifications must be used within a NotificationProvider",
    );
  }
  return context;
};

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({
  children,
}) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    if (isAuthenticated) {
      loadNotifications();
      const interval = setInterval(loadNotifications, 60000); // Refresh every 60 seconds
      return () => clearInterval(interval);
    }
  }, [isAuthenticated]);

  const loadNotifications = async () => {
    try {
      const response = await axios.get("/api/notifications");
      setNotifications(response.data.notifications);
      setUnreadCount(response.data.unread_count);
    } catch (error) {
      console.error("Error loading notifications:", error);
    }
  };

  const deleteNotification = async (id: number) => {
    try {
      const resp = await axios.delete(`/api/notifications/${id}`);
      await loadNotifications();
      // notify other parts of the app (e.g. dashboard) that a notification was deleted
      try {
        const trackerId = resp.data?.tracker_id;
        window.dispatchEvent(new CustomEvent("notificationDeleted", { detail: { trackerId } }));
      } catch (e) {
        // ignore
      }
    } catch (error) {
      console.error("Error deleting notification:", error);
    }
  };

  return (
    <NotificationContext.Provider
      value={{ notifications, unreadCount, loadNotifications, deleteNotification }}
    >
      {children}
    </NotificationContext.Provider>
  );
};
